import type { Editor } from '@tiptap/core';
import type { Attrs, Fragment, NodeRange, NodeType, Node as PMNode } from '@tiptap/pm/model';
import type { Selection } from '@tiptap/pm/state';
import { findWrapping } from '@tiptap/pm/transform';
import expandCellSelection from './expandCellSelection.helper';
import selectionHasNode from './selectionHasNode.helper';
import {
  ALL_TEMPLATE_FIELD_NAMES,
  TEMPLATE_FIELD_BLOCK_NAME,
  TEMPLATE_FIELD_INLINE_NAME,
} from '../doc-engine/templateFields';
import { ancestorsAt, violatesTemplateFieldNesting } from '../tiptap/plugins/getNestedInsertionGuardPlugin';

/** Would a new `fieldType` holding `content`, placed under `ancestors`, break a template field rule? */
const wouldBreakFieldNesting = (fieldType: NodeType, content: Fragment, ancestors: readonly PMNode[]): boolean =>
  violatesTemplateFieldNesting(fieldType.create(null, content), ancestors);

/**
 * The blocks a new block field wraps - the selection widened to its whole list or table - and the wrapping.
 * The insert and the Insert menu both read it, so the menu never disagrees with what the insert does.
 */
export const findBlockFieldWrap = (
  doc: PMNode,
  selection: Selection,
  fieldType: NodeType,
  attrs?: Attrs
): { range: NodeRange; wrapping: NonNullable<ReturnType<typeof findWrapping>> } | null => {
  const { from, to } = expandCellSelection(selection);
  const range = doc.resolve(from).blockRange(doc.resolve(to));
  const wrapping = range && findWrapping(range, fieldType, attrs);
  return range && wrapping ? { range, wrapping } : null;
};

export const selectionHasTemplateField = (editor: Editor): boolean =>
  selectionHasNode(editor, ALL_TEMPLATE_FIELD_NAMES);

/**
 * Would a block field around the selection be refused? Either the blocks cannot be wrapped, or the wrapped
 * content breaks a field rule the nesting guard enforces, like a heading in a table cell.
 */
export const checkBlockFieldIncompatible = (editor: Editor): boolean => {
  const blockFieldType = editor.schema.nodes[TEMPLATE_FIELD_BLOCK_NAME];
  if (editor.state.selection.empty || !blockFieldType) return false;
  const target = findBlockFieldWrap(editor.state.doc, editor.state.selection, blockFieldType);
  if (!target) return true;
  const { range } = target;
  const wrapped = editor.state.doc.slice(range.start, range.end).content;
  return wouldBreakFieldNesting(blockFieldType, wrapped, ancestorsAt(range.$from).slice(0, range.depth + 1));
};

/**
 * Would an inline field around the selection nest a field inside a field? The inline insert and the dropdown
 * replace exactly the selection, never the list or table around it.
 */
export const checkInlineFieldIncompatible = (editor: Editor): boolean => {
  const { selection, doc } = editor.state;
  const inlineFieldType = editor.schema.nodes[TEMPLATE_FIELD_INLINE_NAME];
  if (selection.empty || !inlineFieldType) return false;
  return wouldBreakFieldNesting(
    inlineFieldType,
    doc.slice(selection.from, selection.to).content,
    ancestorsAt(selection.$from)
  );
};
